import proj4 from 'proj4';

const CONTENT_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const NVE_GRID_API_BASE = 'https://gts.nve.no/api/GridTimeSeries';

proj4.defs('EPSG:25833', '+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs');

export interface SnowDepthResult {
  depthCm: number;
  observedAt?: string;
}

const snowCache = new Map<string, SnowDepthResult | null>();

const cacheKey = (lat: number, lon: number) => `${lat.toFixed(4)},${lon.toFixed(4)}`;

const toDateOnly = (date: Date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fetchJsonWithProxyFallback = async (url: string): Promise<any> => {
  const prefersProxy = /gts\.nve\.no/i.test(url);

  const tryDirect = async (): Promise<any | null> => {
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (res.ok) return await res.json();
    } catch { /* silent — CORS or network error */ }
    return null;
  };

  const tryProxy = async (): Promise<any | null> => {
    try {
      const proxiedUrl = `${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`;
      const res = await fetch(proxiedUrl, { headers: { Accept: 'application/json' } });
      if (res.ok) return await res.json();
    } catch { /* silent */ }
    return null;
  };

  if (prefersProxy) {
    // NVE blocks direct CORS requests — only use proxy to avoid 400 console noise
    const proxyResult = await tryProxy();
    if (proxyResult !== null) return proxyResult;
  } else {
    const directResult = await tryDirect();
    if (directResult !== null) return directResult;
    const proxyResult = await tryProxy();
    if (proxyResult !== null) return proxyResult;
  }

  throw new Error('Both direct and proxy fetch failed for snow data');
};

const extractLatestSnowDepth = (payload: any): SnowDepthResult | null => {
  const arr = Array.isArray(payload?.Data) ? payload.Data : [];
  if (arr.length === 0) return null;
  const noData = Number(payload?.NoDataValue);

  for (let i = arr.length - 1; i >= 0; i--) {
    const value = Number(arr[i]);
    if (!Number.isFinite(value)) continue;
    if (Number.isFinite(noData) && value === noData) continue;
    if (value <= 0) return null;
    return {
      depthCm: Math.round(value * 10) / 10,
      observedAt: typeof payload?.EndDate === 'string' ? payload.EndDate : undefined
    };
  }
  return null;
};

const fetchSnowAtProjectedPoint = async (xInt: number, yInt: number, start: Date, end: Date): Promise<SnowDepthResult | null> => {
  const url = `${NVE_GRID_API_BASE}/${xInt}/${yInt}/${toDateOnly(start)}/${toDateOnly(end)}/sd.json`;
  const payload = await fetchJsonWithProxyFallback(url);
  return extractLatestSnowDepth(payload);
};

export const fetchSnowDepth = async (lat: number, lon: number): Promise<SnowDepthResult | null> => {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const key = cacheKey(lat, lon);
  if (snowCache.has(key)) return snowCache.get(key)!;

  try {
    const [x, y] = proj4('EPSG:4326', 'EPSG:25833', [lon, lat]);
    const xInt = Math.round(x);
    const yInt = Math.round(y);

    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 1);
    const end = new Date();
    end.setUTCDate(end.getUTCDate() + 1);

    let result = await fetchSnowAtProjectedPoint(xInt, yInt, start, end);
    if (!result) {
      const fallbackOffsets: Array<[number, number]> = [
        [250, 0],
        [-250, 0],
        [0, 250],
        [0, -250]
      ];
      for (const [dx, dy] of fallbackOffsets) {
        result = await fetchSnowAtProjectedPoint(xInt + dx, yInt + dy, start, end);
        if (result) break;
      }
    }

    snowCache.set(key, result);
    return result;
  } catch (err) {
    console.debug('[snowService] failed to fetch snow depth', err);
    snowCache.set(key, null);
    return null;
  }
};
